/**
 * Helpers for resolving a product's frame image URL and ranking similar frames.
 * Adapted for the ecommerce Product shape (image_uri is already an array of full
 * CloudFront URLs, so no S3->CF transform or CORS proxy is needed).
 */

import type { Product } from '../../types';
import { shapeToRecommendedFrames, type FaceShape } from './faceShape';

/**
 * The image to wear on a face: the admin panel's cutout when there is one, else the
 * catalogue photograph.
 *
 * The cutout is why enhancement exists. Without this the overlay was the product shot with
 * its background intact, so a frame photographed on white arrived as a white rectangle
 * pasted across the wearer's face — the frame drawn correctly inside a card that hid
 * everything around it.
 */
export function resolveFrameUrl(product: Product): string | null {
  if (product.tryonImageUrl) return product.tryonImageUrl;
  const imgs = product.image_uri;
  if (imgs && imgs.length > 0 && imgs[0]) return imgs[0];
  return null;
}

// Categories that can be worn on the face (so try-on makes sense).
// 1 = Eyeglasses, 3 = Sunglasses, 6 = Blue Light Glasses.
// Excluded: 2 = Lens, 4 = Contact Lenses, 5 = Accessories.
const TRYON_CATEGORIES = new Set([1, 3, 6]);

/**
 * Whether virtual try-on should be offered for a product. Requires:
 *  - the product to be switched on for try-on in the admin panel, and
 *  - a face-wearable category (eyeglasses / sunglasses / blue-light), and
 *  - a genuine front/main frame image (type === 'main') to overlay.
 *
 * The admin switch comes first because it is a decision somebody made about this frame,
 * where the other two are only about whether try-on could work at all. It is opt-in: a
 * product with the flag missing or false gets no button, which is what makes an unenhanced
 * or deliberately held-back frame stay out of try-on.
 *
 * The single gate for the card button, the detail page button and the try-on carousel, so
 * all three agree without any of them repeating the rule.
 */
export function canTryOn(product: Product): boolean {
  if (product.tryon_enabled !== true) return false;
  if (!TRYON_CATEGORIES.has(product.category_id)) return false;
  if (product.hasMainImage === false) return false;
  return !!resolveFrameUrl(product);
}

/**
 * Frame image URL for the WebGL try-on. Loaded with crossOrigin="anonymous" so
 * the canvas stays exportable (untainted) for snapshots. The image CDN now sends
 * Access-Control-Allow-Origin (a CORS Response Headers Policy is attached to the
 * specs CloudFront distribution), so the image can be loaded directly — no
 * same-origin /img-proxy indirection is needed. Returns null if no image.
 */
export function proxyFrameUrl(product: Product): string | null {
  return resolveFrameUrl(product);
}

/**
 * Pick up to `count` frames similar to `target`, ranked by shared attributes
 * (frame shape, then frame type, then same category).
 *
 * When `onlyRelated` is true, frames that share NOTHING with the target (score 0)
 * are dropped — use this for a focused "similar frames" set. When false (default),
 * unrelated frames are kept at the end so the full catalogue can be browsed.
 */
export function similarFrames(
  target: Product,
  all: Product[],
  count = 5,
  onlyRelated = false,
): Product[] {
  return all
    .filter((p) => p.id !== target.id && resolveFrameUrl(p))
    .map((p) => {
      let score = 0;
      if (target.frame_shape_id && p.frame_shape_id === target.frame_shape_id) score += 3;
      if (target.frame_type_id && p.frame_type_id === target.frame_type_id) score += 2;
      if (p.category_id === target.category_id) score += 1;
      return { p, score };
    })
    .filter((x) => !onlyRelated || x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((x) => x.p);
}

/**
 * Whether a product's frame shape is recommended for the given face shape.
 * Used both to reorder the grid and to tag "Good fit" frames.
 */
export function isGoodFitForShape(product: Product, shape: FaceShape): boolean {
  if (!product.frame_shape_id) return false;
  return shapeToRecommendedFrames(shape).includes(product.frame_shape_id);
}

/**
 * Stable reorder of `products` so frames whose shape suits the detected face
 * shape lead, ranked by the recommendation order (best-fit first). Products with
 * no/unmatched shape keep their original relative order at the end. Pure — returns
 * a new array and never drops any product.
 */
export function rankFramesForShape(shape: FaceShape, products: Product[]): Product[] {
  const order = shapeToRecommendedFrames(shape);
  const rank = (p: Product): number => {
    const i = p.frame_shape_id ? order.indexOf(p.frame_shape_id) : -1;
    return i === -1 ? order.length : i; // unmatched → after all ranked shapes
  };
  return products
    .map((p, idx) => ({ p, idx, r: rank(p) }))
    .sort((a, b) => (a.r - b.r) || (a.idx - b.idx)) // tie-break preserves input order
    .map((x) => x.p);
}
